import {Header, Layout} from "@components/common";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {fetchJson, getShopNavigation, redirectTo404Page} from "../index";
import Article from "@components/CmsBlocks/Article";
import {useState} from "react";
import {Button} from "@components/ui";
import cn from "clsx";
import s from "./news.module.scss";
import Link from "next/link";
import {useTranslation} from "next-i18next";
import {getCategoryPath} from "@lib/search";
import {useRouter} from "next/router";

const PAGE_SIZE = 9;

export function getArticles(locale: string, page: number, pageSize: number, search: string | null = null, U19 = false, tag = false) {
  return new Promise(async (resolve) => {
    let url = `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"}/api/articles?populate=*&sort[0]=Date%3Adesc&pagination[page]=${page}&pagination[pageSize]=${pageSize}&locale=${locale}`;
    if (search) {
      url += "&filters[title][$containsi]=" + search;
    }
    if (U19) {
      url += '&filters[$and][0][Tags][$containsi]=' + 'U19'
    } else {
      url += '&filters[$and][0][Tags][$notContainsi]=' + 'U19'
    }
    if (tag) {
      url += '&filters[$and][1][Tags][$containsi]=' + tag
    }
    const articles: any = await fetchJson(
      url,
      null
    );
    resolve(articles);
  });
}

export async function getServerSideProps(context: any) {
  try {
    const { locale } = context;
    const url = `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"}`;

    const articles = await getArticles(locale, 1, PAGE_SIZE);

    return {
      props: {
        ...(await serverSideTranslations(locale, ["common", "home"])),
        ...(await getShopNavigation(locale)), // makes "ShopNavigationColumn" property available on page props
        articles,
        locale,
        U19: false
      }
    };
  } catch (error) {
    console.error(error)
    return  redirectTo404Page();
  }
}

export default function News(data: any) {
  const categories = [
    "Partner",
    "Team interviews",
    "Team news",
    "Press releases",
    "Recipes"
  ];
  let [articles, setArticles] = useState(data.articles);
  let [locale, setLocale] = useState(data.locale);
  let [page, setPage] = useState(1);
  let [pageSize, setPageSize] = useState(PAGE_SIZE);
  let [searchString, setSearchString] = useState();
  let [U19, setU19] = useState(data.U19);
  const router = useRouter();
  const query = router.query;
  const [activeFilter, setActiveFilter] = useState(query?.category ?? '');
  const [toggleFilter, setToggleFilter] = useState(false)

  async function previousPage() {
    page = page - 1;
    setPage(page);
    const articles = await getArticles(locale, page, pageSize, null, U19);
    setArticles(articles);
  }
  async function nextPage() {
    page = page + 1;
    setPage(page);
    const articles = await getArticles(locale, page, pageSize, null, U19);
    setArticles(articles);
  }
  async function searchArticles() {
    page = 1;
    setPage(page);
    const articles = await getArticles(locale, page, pageSize, searchString, U19);
    setArticles(articles);
  }

  function changedSearchString(event: any) {
    setSearchString(event.target.value);
  }

  const handleClickDropdown = (event: any, filter: string) => {
    if (filter !== activeFilter) {
      setToggleFilter(true)
    } else {
      setToggleFilter(!toggleFilter)
    }
    setActiveFilter(filter);
  }

  const handleClick = async (event: any, filter: any) => {
    if (filter !== activeFilter) {
      setToggleFilter(true)
    } else {
      setToggleFilter(!toggleFilter)
      filter = '';
    }
    setActiveFilter(filter);
    const articles = await getArticles(locale, page, pageSize, searchString, U19, filter)
    setArticles(articles);
  }
  const { t } = useTranslation("common");
  return (
    <>
    <div className="container-rg">
      <Header></Header>
      <div className={s.searchBar}>
        <label className="hidden" htmlFor={'search'}>
          {t("search")}
        </label>
        <input type="text"
               value={searchString}
               id={'search'}
               className={s.input}
               onChange={(e) => { changedSearchString(e) }}
               placeholder={t("search-articles")}
               aria-label="Search"/>
        <Button variant={"ghost"} onClick={(e) => { searchArticles() }}>
          <svg className={s.icon} fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            />
          </svg>
        </Button>
      </div>
      <main className={s.body}>
        <aside>
          <div className="relative inline-block w-full">
            <div className="lg:hidden">
              <span className="rounded-md shadow-sm">
                <button
                  type="button"
                  onClick={(e) => handleClickDropdown(e, 'categories')}
                  className="flex justify-between w-full rounded-sm border border-accent-3 px-4 py-3 bg-accent-0 text-sm leading-5 font-medium text-accent-4 hover:text-accent-5 focus:outline-none focus:border-blue-300 focus:shadow-outline-normal active:bg-accent-1 active:text-accent-8 transition ease-in-out duration-150"
                  id="options-menu"
                  aria-haspopup="true"
                  aria-expanded="true"
                >
                  {activeFilter
                    ? `Category: ${activeFilter}`
                    :  t("all-categories") }
                  <svg
                    className="-mr-1 ml-2 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            </div>
            <div
              className={`origin-top-left absolute lg:relative left-0 mt-2 w-full rounded-md shadow-lg lg:shadow-none z-10 mb-10 lg:block ${
                activeFilter !== 'categories' || toggleFilter !== true
                  ? 'hidden'
                  : ''
              }`}
            >
              <div className="rounded-sm bg-accent-0 shadow-xs lg:bg-none lg:shadow-none">
                <div
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  <ul>
                    <li
                      className={cn(
                        'block text-sm leading-5 text-accent-4 lg:text-base lg:no-underline lg:font-bold lg:tracking-wide hover:bg-accent-1 lg:hover:bg-transparent hover:text-accent-8 focus:outline-none focus:bg-accent-1 focus:text-accent-8',
                        {
                          underline: !activeFilter,
                        }
                      )}
                    >
                      <Link legacyBehavior
                            href={{ pathname: '' }}
                      >
                        <a
                          onClick={(e) => handleClick(e, null)}
                          className={
                            'block lg:inline-block px-4 py-2 lg:p-0 lg:my-2 lg:mx-4'
                          }
                        >
                          {t("all-categories")}
                        </a>
                      </Link>
                    </li>
                    {categories.map((cat: any) => (
                      <li
                        key={cat}
                        className={cn(
                          'block text-sm leading-5 text-accent-4 hover:bg-accent-1 lg:hover:bg-transparent hover:text-accent-8 focus:outline-none focus:bg-accent-1 focus:text-accent-8',
                          {
                            underline: activeFilter === cat,
                          }
                        )}
                      >
                        <Link legacyBehavior
                              href={{
                                pathname: '',
                              }}
                        >
                          <a
                            onClick={(e) => handleClick(e, cat)}
                            className={
                              'block lg:inline-block px-4 py-2 lg:p-0 lg:my-2 lg:mx-4'
                            }
                          >
                            {cat}
                          </a>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </aside>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 news-line-clamp">
          { articles?.data && articles.data.slice(0).map((article: any, index: any) => {
            return <Article key={`article-index-${index}`} { ...article } />
          } )
          }
        </div>
      </main>
      <div className="text-center mt-6 mb-10">
      {
        articles?.meta?.pagination && <div>
          <Button className="mr-3"
                  variant={'ghost'}
                  onClick={(e) => { previousPage() }}
                  disabled={page === 1}>
            {t("previous")}
          </Button>
          <Button variant={'ghost'}
                  onClick={(e) => { nextPage() }}
                  disabled={articles.meta.pagination.pageCount < 2 || page === articles.meta.pagination.pageCount}>
            {t("next")}
          </Button>
        </div>
      }
      </div>
    </div>
    </>
  )
}

News.Layout = Layout
